import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'api/apiCommon'
import {
  type UserDB,
  transformUserGroupsData,
  transformUsersData,
  type UserGroupDB,
} from 'common/utils/data-transformator'
import { type User, type UserGroup, Role, UserStatus } from 'common/utils/types'
import { type InferType, object, string } from 'yup'

const USERS_PER_PAGE = 11

const addUserGroupSchema = object({
  email: string().trim().email('Invalidan email').required('Obavezno polje').default(''),
  name: string().trim().required('Obavezno polje').default(''),
  nickname: string().trim().required('Obavezno polje').default(''),
  firstName: string().trim().required('Obavezno polje').default(''),
  lastName: string().trim().required('Obavezno polje').default(''),
})

export type AddUserGroupForm = InferType<typeof addUserGroupSchema>

interface usersParams {
  search?: string
  role?: string
  status?: UserStatus
  pibSearch?: string
  page?: number
  ipp?: number
  certificate?: number
}

export const roleText = (role: Role): string => {
  switch (role) {
    case Role.admin:
      return 'admin'
    case Role.consultant:
      return 'consultant'
    case Role.stakeholder:
      return 'stakeholder'
    case Role.company:
      return 'company'
    case Role.vendor:
      return 'vendor'
    default:
      return ''
  }
}

export const getUsers = createAsyncThunk(
  '/users/getUsers',
  async (payload: {
    search?: string
    pibSearch?: string
    roles: Role[]
    status?: UserStatus
    page?: number
    certificate?: number
  }): Promise<{ users: User[]; count: number; roles: Role[] }> => {
    const params: usersParams = {}

    const roleParams = payload?.roles?.map((r) => `role=${roleText(r)}`).join('&')

    let queryString = ''
    if (roleParams) {
      queryString += roleParams
    }

    if (payload?.search) {
      params.search = encodeURIComponent(payload.search)
    }
    if (payload?.pibSearch) {
      params.pibSearch = encodeURIComponent(payload.pibSearch)
    }
    if (payload?.status) {
      params.status = payload?.status
    }
    if (payload?.certificate) {
      params.certificate = payload?.certificate
    }
    if (payload?.page) {
      params.page = payload?.page
    } else {
      params.page = 0
    }
    params.ipp = USERS_PER_PAGE

    queryString += '&' + new URLSearchParams(params as Record<string, string>).toString()

    return await new Promise((resolve, reject) => {
      axios
        .get<{ data: UserDB[]; count: number; role: Role }>(`users?${queryString}`)
        .then((data) => {
          resolve(transformUsersData(data.data, data.count, payload.roles))
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

export const getUserGroups = createAsyncThunk(
  '/users/getUserGroups',
  async (payload: {
    search?: string
    role: Role
    status?: UserStatus
    page?: number
  }): Promise<{ userGroups: UserGroup[]; count: number }> => {
    const params: usersParams = {}
    if (payload?.search) {
      params.search = payload?.search
    }
    if (payload?.status) {
      params.status = payload?.status
    }
    if (payload?.page) {
      params.page = payload?.page
    } else {
      params.page = 0
    }
    params.ipp = USERS_PER_PAGE
    return await new Promise((resolve, reject) => {
      axios
        .get<{ data: UserGroupDB[]; count: number }>(
          `${process.env.REACT_APP_USERS_API_URL ?? process.env.REACT_APP_REST_API_URL ?? ''}/users/groups`,
          { params },
        )
        .then((data) => {
          resolve(transformUserGroupsData(data.data, data.count))
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

interface AddUserPayload {
  firstName?: string
  lastName?: string
  email: string
  role: Role
  id?: string
  reminder?: boolean
}
export const addUser = createAsyncThunk(
  '/users/addUser',
  async (payload: AddUserPayload): Promise<User & { reminder?: boolean }> => {
    const data = await axios.post<{ id: string }>('users/new-user', {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      role: roleText(payload.role),
      groupId: payload.id,
    })
    return {
      ...payload,
      id: data.id,
      status: UserStatus.pending,
      name: { first: payload.firstName ?? ' ', last: payload.lastName ?? '' },
    }
  },
)

export const changeUserStatus = createAsyncThunk(
  '/users/changeUserStatus',
  async (payload: {
    id: string
    role: Role
    status: UserStatus
  }): Promise<{ id: string; role: Role; status: UserStatus }> => {
    return await new Promise((resolve, reject) => {
      axios
        .put(`users/${payload.id}/status`, { status: payload.status })
        .then((data) => {
          resolve({ id: payload.id, role: payload.role, status: payload.status })
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

export const changeUserGroupStatus = createAsyncThunk(
  '/users/changeUserGroupStatus',
  async (payload: { id: string; status: UserStatus }): Promise<{ id: string; status: UserStatus }> => {
    return await new Promise((resolve, reject) => {
      axios
        .put(`users/groups/${payload.id}/status`, { status: payload.status })
        .then((data) => {
          resolve({ id: payload.id, status: payload.status })
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

export const addUserGroup = createAsyncThunk(
  '/users/addUserGroup',
  async (payload: AddUserGroupForm): Promise<UserGroup> => {
    return await new Promise((resolve, reject) => {
      axios
        .post<{ groupId: string; userId: string }>('users/new-stakeholder-group', {
          email: payload.email,
          groupName: payload.name,
          groupSlug: payload.nickname,
          name: {
            first: payload.firstName,
            last: payload.lastName,
          },
        })
        .then((data) => {
          resolve({ ...payload, id: data.groupId, role: Role.stakeholder, status: UserStatus.pending })
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

export const addConsultantCertificate = createAsyncThunk(
  '/users/addConsultantCertificate',
  async (payload: {
    id: string
    certificateId: number | undefined
    validFrom: Date
    validFor: number
  }): Promise<void> => {
    await new Promise((resolve, reject) => {
      axios
        .post(`users/${payload.id}/add-certificate`, {
          certificateId: payload.certificateId,
          validFrom: payload.validFrom,
          validFor: payload.validFor,
        })
        .then(() => {
          resolve({
            id: payload.id,
            certificateId: payload.certificateId,
            validFrom: payload.validFor,
            validFor: payload.validFor,
          })
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

export interface EditUserEmailPayload {
  id: string
  email?: string
  aprEmail?: string
}

export const editUserEmail = createAsyncThunk(
  '/users/editUserEmail',
  async (payload: EditUserEmailPayload): Promise<void> => {
    await new Promise((resolve, reject) => {
      axios
        .put(`users/${payload.id}/email`, { email: payload.email, aprEmail: payload.aprEmail })
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)
